import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EInvoiceChip } from '@/components/einvoicing/einvoice-chip';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  FILTERS,
  canResubmit,
  einvoiceFilterLabel,
  formatDateTime,
  matchesFilter,
  useEInvoices,
  useEInvoicingSettings,
  useResubmit,
  type EInvoiceFilter,
  type EInvoiceRow,
} from '@/lib/einvoicing';
import { formatAmount, formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { ltr } from '@/lib/arabic';
import { i18n, useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const KEYS = FILTERS.map(([k]) => k);

export const Route = createFileRoute('/taxes/e-invoicing')({
  component: EInvoicing,
  validateSearch: (s: Record<string, unknown>): { status?: EInvoiceFilter } =>
    KEYS.includes(s['status'] as EInvoiceFilter) ? { status: s['status'] as EInvoiceFilter } : {},
});

const docPath = (r: EInvoiceRow) =>
  r.kind === 'credit_note'
    ? `/sales/credit-notes/${r.documentId}`
    : `/sales/invoices/${r.documentId}`;

/**
 * E-invoicing exceptions (document 06 "E-invoicing"): what the service provider or local
 * validation rejected and deliveries that used up their retries, with the reason and Resubmit;
 * chips for the other states. Only reachable while e-invoicing is on for the company.
 */
function EInvoicing() {
  const t = useT();
  const orgId = useOrgId();
  const settings = useEInvoicingSettings();
  const rows = useEInvoices();
  const navigate = useNavigate();
  const { status = 'exceptions' } = Route.useSearch() as { status?: EInvoiceFilter };
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const all = rows.data ?? [];
  const shown = all.filter(
    (r) =>
      matchesFilter(r, status) &&
      (!needle ||
        r.number.toLowerCase().includes(needle) ||
        r.contactName.toLowerCase().includes(needle) ||
        (r.aspReference ?? '').toLowerCase().includes(needle)),
  );
  const count = (f: EInvoiceFilter) => all.filter((r) => matchesFilter(r, f)).length;

  return (
    <>
      <PageHeader
        title={t('taxPages.eInvoicing.title')}
        description={t('taxPages.eInvoicing.description')}
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : settings.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !settings.data?.enabled ? (
        <EmptyState
          title={t('taxPages.eInvoicing.offTitle')}
          hint={t('taxPages.eInvoicing.offHint')}
          action={
            <Button variant="outline" asChild>
              <Link to="/settings/company" search={{ tab: 'sales' }}>
                {t('taxPages.eInvoicing.companySettings')}
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label={t('taxPages.eInvoicing.statusLabel')}
          >
            {FILTERS.map(([k]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() =>
                  void navigate({
                    to: '/taxes/e-invoicing',
                    search: k === 'exceptions' ? {} : { status: k },
                  })
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {einvoiceFilterLabel(k)}
                {rows.data ? <span className="ms-1 tnum">({count(k)})</span> : null}
              </button>
            ))}
          </div>
          <div className="mb-3 relative w-[340px]">
            <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              className="bg-card ps-8"
              placeholder={t('taxPages.eInvoicing.searchPlaceholder')}
              aria-label={t('taxPages.eInvoicing.searchLabel')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {rows.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : rows.isError ? (
            <EmptyState title={t('taxPages.eInvoicing.loadError')} hint={rows.error.message} />
          ) : shown.length === 0 ? (
            <EmptyState
              title={
                status === 'exceptions'
                  ? t('taxPages.eInvoicing.noExceptions')
                  : t('taxPages.eInvoicing.noMatch')
              }
              hint={status === 'exceptions' ? t('taxPages.eInvoicing.noExceptionsHint') : undefined}
            />
          ) : (
            <ExceptionsTable rows={shown} onOpen={(r) => void navigate({ to: docPath(r) })} />
          )}
        </>
      )}
    </>
  );
}

function ExceptionsTable({
  rows,
  onOpen,
}: {
  rows: EInvoiceRow[];
  onOpen: (r: EInvoiceRow) => void;
}) {
  const t = useT();
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader className="sticky top-0 bg-card">
          <TableRow>
            <TableHead className="w-[100px]">{t('taxPages.eInvoicing.col.date')}</TableHead>
            <TableHead className="w-[120px]">{t('taxPages.eInvoicing.col.number')}</TableHead>
            <TableHead>{t('taxPages.eInvoicing.col.customer')}</TableHead>
            <TableHead className="w-[120px] text-end">
              {t('taxPages.eInvoicing.col.total')}
            </TableHead>
            <TableHead className="w-[140px]">{t('taxPages.eInvoicing.col.einvoice')}</TableHead>
            <TableHead className="min-w-[220px]">{t('taxPages.eInvoicing.col.details')}</TableHead>
            <TableHead className="w-[130px]">{t('taxPages.eInvoicing.col.lastUpdate')}</TableHead>
            <TableHead className="w-[130px] text-end">
              {t('taxPages.eInvoicing.col.action')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.submissionId}
              className="cursor-pointer"
              onClick={() => onOpen(r)}
              data-testid="einvoice-row"
            >
              <TableCell>{formatDate(r.issueDate)}</TableCell>
              <TableCell
                className="tnum"
                title={
                  r.kind === 'credit_note'
                    ? t('taxPages.eInvoicing.creditNote')
                    : t('taxPages.eInvoicing.invoice')
                }
              >
                {r.number}
              </TableCell>
              <TableCell className="font-medium whitespace-normal">{r.contactName}</TableCell>
              <TableCell className="tnum text-end">
                {r.kind === 'credit_note' ? '-' : ''}
                {formatAmount(r.total)}
              </TableCell>
              <TableCell>
                <EInvoiceChip status={r.status} needsAttention={r.needsAttention} />
              </TableCell>
              <TableCell className="text-sm whitespace-normal text-muted-foreground">
                <span className="line-clamp-2">{reason(r)}</span>
              </TableCell>
              <TableCell className="text-sm whitespace-normal tnum">
                {formatDateTime(r.updatedAt)}
              </TableCell>
              <TableCell className="text-end" onClick={(e) => e.stopPropagation()}>
                <RowAction r={r} onOpen={() => onOpen(r)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function reason(r: EInvoiceRow): string {
  if (r.errors.length) return r.errors.map((e) => e.message).join('; ');
  if (r.needsAttention)
    return i18n.t('taxPages.eInvoicing.deliveryFailed', {
      count: r.attempts,
      error: r.lastError ?? i18n.t('taxPages.einvoice.providerUnavailable'),
    });
  if (r.status === 'pending' && r.lastError)
    return i18n.t('taxPages.eInvoicing.retrying', { error: r.lastError });
  if (r.status === 'not_required') return r.statusReason ?? '';
  if (r.aspReference)
    return i18n.t('taxPages.einvoice.event.reference', { reference: ltr(r.aspReference) });
  return '';
}

function RowAction({ r, onOpen }: { r: EInvoiceRow; onOpen: () => void }) {
  const me = useMe();
  const t = useT();
  const resubmit = useResubmit();
  if (me.data?.isStaff && canResubmit(r))
    return (
      <Button
        size="sm"
        variant="link"
        className="px-2"
        disabled={resubmit.isPending}
        onClick={() =>
          resubmit.mutate(r.documentId, {
            onSuccess: () =>
              toast(
                r.status === 'rejected'
                  ? t('taxPages.eInvoicing.toastResubmitted', { number: ltr(r.number) })
                  : t('taxPages.eInvoicing.toastQueued', { number: ltr(r.number) }),
              ),
            onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
          })
        }
      >
        {r.status === 'rejected'
          ? t('taxPages.einvoice.resubmit')
          : t('taxPages.einvoice.retryDelivery')}
      </Button>
    );
  return (
    <Button size="sm" variant="link" className="px-2" onClick={onOpen}>
      {t('taxPages.eInvoicing.view')}
    </Button>
  );
}
